import { yupResolver } from "@hookform/resolvers/yup";
import { iMerchant } from "core/kangarooTypes/iMerhant";
import { iBusinessFormData } from "core/kangarooTypes/ProfileManagement";
import { updateBusinessInfo } from "core/Services/MerchantServices";
import { ChevronDown, Loader, Save } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import Button from "shared/Button";
import FormInput from "shared/FormInput";
import { USER_EMAIL, USER_ID, USER_NAME } from "utils/constant";
import * as yup from "yup";
import { useTranslation } from "react-i18next";

interface BusinessFormTemp extends iBusinessFormData {
  phone: string;
  secondary_phone: string;
}

const BusinessInfoForm = ({
  initialData,
}: {
  initialData: iMerchant | null;
}) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userId, setUserId] = useState<string>();
  const BusinessInfoSchema = yup.object().shape({
    business_name: yup
      .string()
      .required(t("businessInfoForm.validation.businessNameRequired")),
    first_name: yup
      .string()
      .required(t("businessInfoForm.validation.firstNameRequired")),
    last_name: yup
      .string()
      .required(t("businessInfoForm.validation.lastNameRequired")),
    email: yup
      .string()
      .email(t("businessInfoForm.validation.invalidEmail"))
      .required(t("businessInfoForm.validation.emailRequired")),
    phone: yup
      .string()
      .required(t("businessInfoForm.validation.phoneCodeRequired"))
      .matches(/^\+\d+$/, t("businessInfoForm.validation.invalidPhoneCode")),
    phone_number: yup
      .string()
      .required(t("businessInfoForm.validation.phoneNumberRequired"))
      .matches(/^\d+$/, t("businessInfoForm.validation.phoneNumberDigits")),
    secondary_phone: yup
      .string()
      .required(t("businessInfoForm.validation.phoneCodeRequired"))
      .matches(/^\+\d+$/, t("businessInfoForm.validation.invalidPhoneCode")),
    secondary_phone_number: yup
      .string()
      .optional()
      .matches(/^\d*$/, t("businessInfoForm.validation.phoneNumberDigits")),
  });
  const phones = [
    { value: "+962", label: "+962 Jordan" },
    { value: "+966", label: "+966 Saudi Arabia" },
    { value: "+971", label: "+971 United Arab Emirates" },
    { value: "+965", label: "+965 Kuwait" },
    { value: "+973", label: "+973 Bahrain" },
    { value: "+974", label: "+974 Qatar" },
    { value: "+968", label: "+968 Oman" },
    { value: "+964", label: "+964 Iraq" },
    { value: "+963", label: "+963 Syria" },
    { value: "+961", label: "+961 Lebanon" },
    { value: "+970", label: "+970 Palestine" },
    { value: "+20", label: "+20 Egypt" },
    { value: "+90", label: "+90 Turkey" },
    { value: "+98", label: "+98 Iran" },
    { value: "+967", label: "+967 Yemen" },
    { value: "+249", label: "+249 Sudan" },
  ];

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<BusinessFormTemp>({
    resolver: yupResolver<BusinessFormTemp>(BusinessInfoSchema),
    defaultValues: {
      business_name: "",
      first_name: "",
      last_name: "",
      email: "",
      phone: "+962",
      phone_number: "",
      secondary_phone: "+962",
      secondary_phone_number: "",
    },
  });

  useEffect(() => {
    if (initialData) {
      setValue("business_name", initialData.business_name);
      setValue("first_name", initialData.first_name);
      setValue("last_name", initialData.last_name);
      setValue("email", initialData.email);
      // Split phone number into code and number if it exists
      if (initialData.phone_number) {
        const phoneCode = phones.find((p) =>
          initialData.phone_number.startsWith(p.value)
        );
        if (phoneCode) {
          setValue("phone", phoneCode.value);
          setValue(
            "phone_number",
            initialData.phone_number.substring(phoneCode.value.length)
          );
        } else {
          setValue("phone", "+962");
          setValue("phone_number", initialData.phone_number);
        }
      }
      // Split secondary phone number into code and number if it exists
      if (
        initialData.secondary_phone_number &&
        initialData.secondary_phone_number.trim()
      ) {
        const phoneCode = phones.find((p) =>
          initialData.secondary_phone_number?.startsWith(p.value)
        );
        if (phoneCode) {
          setValue("secondary_phone", phoneCode.value);
          setValue(
            "secondary_phone_number",
            initialData.secondary_phone_number.substring(phoneCode.value.length)
          );
        } else {
          setValue("secondary_phone", "+962");
          setValue(
            "secondary_phone_number",
            initialData.secondary_phone_number
          );
        }
      }
    }
    const userId = localStorage.getItem(USER_ID);
    setUserId(userId!);
  }, [initialData, setValue, phones]);

  const onSubmit = async (data: BusinessFormTemp) => {
    try {
      setIsSubmitting(true);
      const formData: iBusinessFormData = {
        business_name: data.business_name,
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        phone_number: data.phone + data.phone_number,
        secondary_phone_number: data.secondary_phone_number
          ? data.secondary_phone + data.secondary_phone_number
          : "",
      };
      await updateBusinessInfo(formData, userId!);
      localStorage.setItem(USER_NAME, `${data.first_name} ${data.last_name}`);
      localStorage.setItem(USER_EMAIL, data.email);
      if (initialData) {
        initialData.phone_number = formData.phone_number;
        initialData.secondary_phone_number = formData.secondary_phone_number;
      }
      toast.success(t("businessInfoForm.success"));
    } catch (error) {
      toast.error(t("businessInfoForm.error"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm">
      <div className="flex flex-col md:flex-row justify-between items-center mb-4">
        <div className="text-lg font-medium mb-2 md:mb-0">
          {t("businessInfoForm.title")}
        </div>
        <Button
          icon={
            isSubmitting ? (
              <Loader size={16} className="animate-spin" />
            ) : (
              <Save size={16} />
            )
          }
          onClick={handleSubmit(onSubmit)}
          disabled={isSubmitting}
          className="w-full md:w-auto"
        >
          {isSubmitting
            ? t("businessInfoForm.saving")
            : t("businessInfoForm.saveButton")}
        </Button>
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Commercial Name on one row */}
          <div className="col-span-2">
            <FormInput
              label={t("businessInfoForm.commercialName")}
              name="business_name"
              register={register}
              errors={errors}
            />
          </div>
          {/* First Name and Last Name on the same row */}
          <FormInput
            label={t("businessInfoForm.firstName")}
            name="first_name"
            register={register}
            errors={errors}
          />
          <FormInput
            label={t("businessInfoForm.lastName")}
            name="last_name"
            register={register}
            errors={errors}
          />
          <FormInput
            label={t("businessInfoForm.contactEmail")}
            name="email"
            type="email"
            register={register}
            errors={errors}
          />
          {/* Phone Number with country code */}
          <div className="flex flex-col">
            <label className="text-sm font-medium mb-1">
              {t("businessInfoForm.phoneNumber")}
            </label>
            <div className="flex gap-2">
              <div className="w-1/4">
                <FormInput
                  name="phone"
                  type="select"
                  register={register}
                  errors={errors}
                  selectOptions={phones}
                  icon={<ChevronDown size={20} />}
                />
              </div>
              <div className="w-3/4">
                <FormInput
                  name="phone_number"
                  type="text"
                  register={register}
                  errors={errors}
                />
              </div>
            </div>
          </div>
          {/* Secondary Phone Number with country code */}
          <div className="flex flex-col">
            <label className="text-sm font-medium mb-1">
              {t("businessInfoForm.secondaryPhoneNumber")}
            </label>
            <div className="flex gap-2">
              <div className="w-1/4">
                <FormInput
                  name="secondary_phone"
                  type="select"
                  register={register}
                  errors={errors}
                  selectOptions={phones}
                  icon={<ChevronDown size={20} />}
                />
              </div>
              <div className="w-3/4">
                <FormInput
                  name="secondary_phone_number"
                  type="text"
                  register={register}
                  errors={errors}
                  placeholder={t("businessInfoForm.optional")}
                />
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default BusinessInfoForm;
